import React from "react"
import PropTypes from "prop-types"
import { jsPDF } from "jspdf"

import EditorJS from "@editorjs/editorjs"
import HeaderTool from "@editorjs/header"
import ListTool from "@editorjs/list"
import Table from "@editorjs/table"

import { DownloadIcon } from "ksc-portal-common-components/atoms"
import { Button } from "ksc-portal-common-components/shadcn"

import TC from "../../termsAndConditionsState"
import Editor from "./editor"

import { TERMS_AND_CONDITIONS } from "../../constants"

class TermsAndConditions extends React.PureComponent {
  constructor() {
    super()
    this.state = { editor: null }
    this.clickedLink = this.clickedLink.bind(this)
    this.download = this.download.bind(this)
  }

  componentDidMount() {
    const { getRequest, storeTerms, tcManager, tcTerms } = this.props
    if (!tcTerms)
      Promise.all(
        tcManager.moduleList.map(
          ({ latest_version, id }) =>
            new Promise((resolve) => {
              getRequest({
                url: `/foundation_v1/terms-and-conditions/${id}/${latest_version}`,
                onSuccess: ({ content }) => resolve({ [id]: content }),
                onFailure: () => {
                  console.error(`Error retrieving ${id} T&C blocks`)
                  resolve({ [id]: { blocks: [] } })
                },
              })
            })
        )
      )
        .then(storeTerms)
        .catch(console.error)

    const editor = new EditorJS({
      holder: `editorjs-tc`,
      tools: {
        header: HeaderTool,
        list: ListTool,
        table: Table,
      },
      logLevel: "INFO",
    })
    editor.isReady
      .then(() => editor.readOnly.toggle())
      .then(() => this.setState({ editor }))
  }

  clickedLink(e) {
    const { history } = this.props
    const { host } = window.location
    const { href } = e.target
    if (href) {
      e.preventDefault()

      const isRelativePath = href.startsWith("/")
      const isSameOrigin = ["kaizenreporting.", "singlerulebook."].some(
        (hubName) => href.includes(hubName) && !href.includes("mailto:")
      )

      if (isRelativePath || isSameOrigin) {
        const destination = href.split(host).pop()
        history.push(destination)
        window.scrollTo(0, 0)
      } else {
        window.open(href, "_blank")
      }
    }
  }

  download() {
    const { tcTerms, module } = this.props
    const [{ id }] = module
    const blocks = tcTerms[id]?.blocks
    const doc = new jsPDF() // eslint-disable-line

    const maxCharsInLine = 40
    const maxLinesInPage = 40

    const textArray = []

    blocks.forEach((el) => {
      const {
        data: { text, items },
      } = el
      if (text) {
        textArray.push(text)
      }
      if (items) {
        items.forEach((item) => textArray.push(item))
      }
    })

    const cleanText = textArray.map((el) =>
      el.replaceAll(/<[^>]*>/g, "").replaceAll("&nbsp;", " ")
    )
    let lines = []

    while (cleanText.length) {
      let line = []
      let count = 0

      const words = cleanText
        .shift()
        .split(" ")
        .reduce((acc, curr) => [...acc, ...curr.split("\n")], [])
      while (words.length) {
        while (count < maxCharsInLine && words.length) {
          const word = words.shift()
          line.push(word)
          count += word.length
        }
        lines.push(line.join(" "))
        count = 0
        line = []
      }
    }

    while (lines.length) {
      const toWrite = lines.slice(0, maxLinesInPage)
      doc.text(toWrite.join("\n"), 10, 10)
      lines = lines.slice(maxLinesInPage)
      if (lines.length) doc.addPage()
    }

    doc.save("download.pdf")
  }

  render() {
    const { editor } = this.state
    const { tcManager, tcTerms, module } = this.props

    const [{ name, id }] = module
    return (
      <div className="flex-column flex-1">
        <div className="terms-div-style legal-component">
          <span className="tc-hr-border">
            <div className="terms-conditions-content-title float-left">
              {`${name} ${TERMS_AND_CONDITIONS}`}
            </div>
            {tcTerms && (
              <Button
                variant="ghost"
                onClick={this.download}
                data-testid="terms-and-conditions"
                className="float-right"
              >
                <DownloadIcon /> Download
              </Button>
            )}
          </span>
          <div className="terms-div-inner" data-testid="tc-container">
            <div
              id="editorjs-tc"
              data-testid="editorjs-tc"
              onClick={this.clickedLink}
              role="button"
              onKeyDown={this.clickedLink}
              tabIndex={0}
              aria-label="editorjs control"
            />
            {editor && tcTerms && (
              <Editor
                key={`foundation-terms-${id}`}
                blocks={tcTerms[id]?.blocks}
                editorRef={editor}
                outstanding={tcManager.isModuleOutstanding(id)}
                accept={() => tcManager.acceptTC(id)}
                reject={() => tcManager.rejectTC(id)}
              />
            )}
          </div>
        </div>
      </div>
    )
  }
}

TermsAndConditions.propTypes = {
  tc: PropTypes.shape({
    outstanding: PropTypes.arrayOf(PropTypes.shape({})),
    modules: PropTypes.shape({}),
    terms: PropTypes.shape({}),
  }),
  module: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    })
  ).isRequired,
  tcTerms: PropTypes.shape({}),
  tcManager: PropTypes.shape(TC).isRequired,
  getRequest: PropTypes.func.isRequired,
  storeTerms: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      module: PropTypes.string,
    }),
  }).isRequired,
}

TermsAndConditions.defaultProps = {
  tc: {},
  tcTerms: null,
}

export default TermsAndConditions
